import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';

const TermsConditions = () => {
  const location = useLocation();
  // Smooth-scroll to section when a hash is present (e.g., #cookies)
  useEffect(() => {
    const hash = location.hash || (typeof window !== 'undefined' ? window.location.hash.split('#')[1] ? `#${window.location.hash.split('#').pop()}` : '' : '');
    if (hash) {
      const id = hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-beige-50 to-rose-50 pt-16">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Link 
            to="/" 
            className="inline-flex items-center text-gray-600 hover:text-gray-800 transition-colors duration-200"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-4xl font-playfair font-bold text-charcoal-900 mb-8">
            Alera – Terms & Conditions
          </h1>
          
          <div className="prose prose-lg max-w-none text-charcoal-700">
            <p className="text-lg leading-relaxed mb-8">
              Welcome to Alera. By using this website, you agree to the following Terms & Conditions. 
              Please read them carefully.
            </p>

            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-playfair font-semibold text-charcoal-900 mb-4">
                  1. General Use
                </h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Alera provides recipes, workouts, resources, and community support for educational and informational purposes.</li>
                  <li>You must be at least 18 years old, or have parental/guardian permission, to use this site.</li>
                  <li>By using Alera, you agree not to misuse the site, post harmful content, or violate community guidelines.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-playfair font-semibold text-charcoal-900 mb-4">
                  2. Health Disclaimer
                </h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>The content on Alera is based on the founder's personal experience with eczema, allergies, and fitness, along with certified expertise as a personal trainer and nutritionist.</li>
                  <li>Alera does not provide medical advice. Information is for general wellness, fitness, and nutrition purposes only.</li>
                  <li>Always consult a doctor or qualified healthcare provider before making changes to your diet, exercise routine, or allergy/eczema management.</li>
                  <li>Participation in workouts, recipes, or other resources is at your own risk.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-playfair font-semibold text-charcoal-900 mb-4">
                  3. Subscriptions & Payments
                </h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Certain areas of Alera are accessible only through a paid membership (Freedom plan).</li>
                  <li>Payments are processed securely through third-party providers.</li>
                  <li>Subscriptions renew automatically unless canceled. You may cancel at any time by contacting us at <a href="mailto:hello@alera.co.nz" className="text-sage-600 hover:text-sage-700 underline">hello@alera.co.nz</a>.</li>
                  <li>
                    <strong>Monthly Memberships:</strong> Cancel anytime. Your access continues until the end of your current billing period, after which no further charges will occur.
                  </li>
                  <li>
                    <strong>Annual Memberships:</strong> Annual memberships are non-refundable. If you cancel before your year is complete, your access will continue until the end of your annual term, but you will not receive a refund for any remaining unused months. Your membership will not renew for the next annual cycle.
                  </li>
                  <li>Digital products (such as recipe books) are for personal use only and may not be shared, resold, or distributed.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-playfair font-semibold text-charcoal-900 mb-4">
                  4. Community Guidelines
                </h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Be respectful to other members. No harassment, spam, or offensive content.</li>
                  <li>Alera reserves the right to remove content or suspend accounts that violate guidelines.</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-playfair font-semibold text-charcoal-900 mb-4">
                  5. Intellectual Property Policy
                </h2>
                <p className="text-charcoal-700 mb-4">Effective Date: 23 September 2025</p>
                <p className="mb-4">All content, materials, and resources on Alera are the exclusive property of Alera and its creator(s). This includes, but is not limited to: recipes, fitness programs, wellness guides, written articles, blogs, images, videos, graphics, logos, trademarks, and brand elements.</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>
                    <strong>Personal Use Only:</strong> Content from Alera is provided for your personal, non-commercial use. You may view, download, or print materials for your own use but may not copy, reproduce, distribute, or share them for commercial purposes.
                  </li>
                  <li>
                    <strong>Prohibited Uses (without prior written consent):</strong>
                    <ul className="list-disc pl-6 mt-2 space-y-1">
                      <li>Republish or resell Alera content</li>
                      <li>Distribute Alera resources on other websites, apps, or platforms</li>
                      <li>Modify or claim Alera content as your own</li>
                    </ul>
                  </li>
                  <li>
                    <strong>Enforcement:</strong> Unauthorized use of Alera’s intellectual property may result in removal of access, termination of membership, and/or legal action.
                  </li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-playfair font-semibold text-charcoal-900 mb-4">
                  6. Limitation of Liability
                </h2>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Alera is not responsible for injuries, allergic reactions, or health issues that may occur from following recipes, workouts, or wellness tips.</li>
                  <li>We do not guarantee specific results from our programs or resources.</li>
                </ul>
              </section>

              <section>
                <h2 id="cookies" className="text-2xl font-playfair font-semibold text-charcoal-900 mb-4">
                  7. Cookie Policy
                </h2>
                <p className="text-charcoal-700 mb-4">Effective Date: 23 September 2025</p>
                <p className="mb-4">Alera uses cookies and similar technologies to enhance your experience and improve our services.</p>
                <h3 className="text-xl font-semibold text-charcoal-900 mt-4 mb-2">What Are Cookies?</h3>
                <p className="mb-4">Cookies are small text files stored on your device when you visit a website. They help websites remember information about your visit, such as preferences or login details.</p>
                <h3 className="text-xl font-semibold text-charcoal-900 mt-4 mb-2">How Alera Uses Cookies</h3>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Essential cookies:</strong> Enable core functionality such as secure login and member access.</li>
                  <li><strong>Performance cookies (optional):</strong> Collect anonymous data to help us understand how visitors use the site and improve user experience.</li>
                  <li><strong>Preference cookies:</strong> Store your settings (such as filters or saved content).</li>
                  <li>We do not use cookies to collect personal health data without your consent.</li>
                </ul>
                <h3 className="text-xl font-semibold text-charcoal-900 mt-4 mb-2">Third-Party Cookies</h3>
                <p className="mb-4">If we use services such as Google Analytics, these providers may set their own cookies. We encourage you to review their privacy policies.</p>
                <h3 className="text-xl font-semibold text-charcoal-900 mt-4 mb-2">Managing Cookies</h3>
                <p className="mb-4">You can control cookies through your browser settings. Please note that disabling cookies may limit functionality, including login and access to member features.</p>
                <p className="mb-4">By using Alera, you consent to the use of cookies as described in this policy.</p>
              </section>

              <section>
                <h2 className="text-2xl font-playfair font-semibold text-charcoal-900 mb-4">
                  8. Changes to Terms
                </h2>
                <p>
                  We may update these Terms from time to time. Continued use of the site means you accept the updated Terms.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-playfair font-semibold text-charcoal-900 mb-4">
                  9. Contact
                </h2>
                <p>
                  For questions, contact us at{' '}
                  <a 
                    href="mailto:hello@alera.co.nz" 
                    className="text-sage-600 hover:text-sage-700 underline"
                  >
                    hello@alera.co.nz
                  </a>.
                </p>
              </section>
            </div>

            <div className="mt-12 p-6 bg-sage-50 rounded-lg border border-sage-200">
              <p className="text-sm text-charcoal-600">
                <strong>Last Updated:</strong> February 2026
              </p>
              <p className="text-sm text-charcoal-600 mt-2">
                If you have any questions about these Terms & Conditions, please contact us at{' '}
                <a 
                  href="mailto:hello@alera.co.nz" 
                  className="text-sage-600 hover:text-sage-700 underline"
                >
                  hello@alera.co.nz
                </a>.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TermsConditions;
